import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { postError } from "@/shared/lib/host-lifecycle";
import { isSessionExpired, subscribeSessionExpired } from "./sessionExpiry";

/** Full-screen terminal state once the session is unrecoverable. */
export function SessionExpiredGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [expired, setExpired] = useState(isSessionExpired());

  useEffect(() => subscribeSessionExpired(() => setExpired(true)), []);

  /**
   * The one failure the host can actually fix: it owns the session, so it
   * prompts re-auth and keeps the merchant on the route instead of showing a
   * dead end. Without this the frame just sat on the screen below and the
   * host had no idea (host team, 2026-08-13).
   *
   * Fires on the LATCH, not on every render — the latch is one-way, so this
   * runs at most once per session.
   */
  useEffect(() => {
    if (expired) postError("SESSION_EXPIRED");
  }, [expired]);

  if (!expired) return <>{children}</>;

  return (
    <div
      data-cy="studio-session-expired"
      className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <h1 className="text-[1.875rem] leading-9 font-semibold">
        {t("templates.studio.sessionExpired.title")}
      </h1>
      <p className="max-w-md text-text-secondary">
        {t("templates.studio.sessionExpired.body")}
      </p>
    </div>
  );
}
